import { useCallback, useEffect, useRef, useState } from "react";

import { adaptProjectsToTree } from "../model/adapters/projectTree.adapter";
import type { ProjectNode } from "../model/types/ProjectTree";
import { projectsApi } from "../api/projects.api";

type Params = {
  limit?: number;
  search?: string;
  enabled?: boolean;
};

function mergeById(prev: ProjectNode[], next: ProjectNode[]) {
  const map = new Map<number, ProjectNode>();
  for (const item of prev) map.set(item.id, item);
  for (const item of next) map.set(item.id, item);
  return Array.from(map.values());
}

export function useProjectsTree({
  limit = 20,
  search = "",
  enabled = true,
}: Params = {}) {
  const [projects, setProjects] = useState<ProjectNode[]>([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeListRequests, setActiveListRequests] = useState(0);
  const [activeTreeRequests, setActiveTreeRequests] = useState(0);

  const loadingMore = activeListRequests > 0;
  const loadingTree = activeTreeRequests > 0;
  const loadingAny = loadingMore || loadingTree;

  const requestedPagesRef = useRef<Set<string>>(new Set());
  const requestEpochRef = useRef(0);

  const projectsRef = useRef<ProjectNode[]>([]);
  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);

  const loadPage = useCallback(
    async (pageNumber: number) => {
      if (!enabled) return;

      const key = `${search}:${limit}:${pageNumber}`;
      if (requestedPagesRef.current.has(key)) return;

      requestedPagesRef.current.add(key);
      setError(null);
      setActiveListRequests((c) => c + 1);

      const requestEpoch = requestEpochRef.current;

      try {
        const res = await projectsApi.list({
          tree: true,
          page: pageNumber,
          limit,
          search,
        });

        if (requestEpoch !== requestEpochRef.current) return;

        setProjects((prev) => mergeById(prev, adaptProjectsToTree(res.data)));
        setHasNext(Boolean(res.page?.has_next));
      } catch (e) {
        if (requestEpoch !== requestEpochRef.current) return;

        const err = e as Error;
        setError(err.message ?? "Failed to fetch projects");
      } finally {
        setActiveListRequests((c) => Math.max(0, c - 1));
      }
    },
    [enabled, limit, search],
  );

  const loadProjectById = useCallback(
    async (projectId: number): Promise<ProjectNode | null> => {
      const existing = projectsRef.current.find((p) => p.id === projectId);

      if (existing && (existing.phases?.length ?? 0) > 0) return existing;

      setError(null);
      setActiveTreeRequests((c) => c + 1);

      const requestEpoch = requestEpochRef.current;

      try {
        const res = await projectsApi.getTree(projectId);
        if (requestEpoch !== requestEpochRef.current) return null;

        const [project] = adaptProjectsToTree([res]);
        setProjects((prev) => mergeById(prev, [project]));
        return project;
      } catch {
        return null;
      } finally {
        setActiveTreeRequests((c) => Math.max(0, c - 1));
      }
    },
    [],
  );

  const ensureProjectLoaded = useCallback(
    (projectId: number) => loadProjectById(projectId),
    [loadProjectById],
  );

  useEffect(() => {
    requestEpochRef.current++;
    requestedPagesRef.current.clear();

    setProjects([]);
    setPage(1);
    setHasNext(true);
    setError(null);

    setActiveListRequests(0);
    setActiveTreeRequests(0);
  }, [search, limit, enabled]);

  useEffect(() => {
    if (!enabled) return;

    if (page === 1 && projects.length === 0) {
      loadPage(1);
      return;
    }

    loadPage(page);
  }, [enabled, page, loadPage, projects.length]);

  const loadMore = useCallback(() => {
    if (!enabled || loadingMore || !hasNext) return;
    setPage((p) => p + 1);
  }, [enabled, loadingMore, hasNext]);

  return {
    projects,
    error,
    hasNext: enabled ? hasNext : false,
    loadingMore,
    loadingTree,
    loadingAny,
    loadMore,
    loadProjectById,
    ensureProjectLoaded,
  };
}
